import {Component,OnInit,ViewChild} from "@angular/core";
import {NgForm} from "@angular/forms";
import {BehaviorSubject} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {TypeCodeErreur} from "@domain/common/http/result";
import {IndemnitesParametres,PourcentageDomaine,ReglesAttributionConf} from "@domain/admin/frais/indemnites/indemnites-parametres";
import {IndemnitesParametresService} from "@services/admin/frais/indemnites-parametres.service";

/**
 * Onglet paramètres
 *
 * @author Guillaume TRAVOT
 * @date 26/04/2024
 */
@Component({
    host: {'data-test-id': 'indemnites-parametres'},
    templateUrl: './indemnites-parametres.component.html',
    providers: [
        IndemnitesParametresService
    ]
})
export class IndemnitesParametresComponent implements OnInit {
    /**Formulaire*/
    @ViewChild('form') form: NgForm;

    /**Données de l'écran*/
    model: IndemnitesParametres = new IndemnitesParametres();

    /**Indique que le chargement est en cours*/
    isLoading: boolean = false;

    /**Indique que le chargement des pourcentages est en cours*/
    isLoadingPourcentages: boolean = false;

    /**Indique que le chargement des délais forfaitaires est en cours*/
    isLoadingReglesConf: boolean = false;

    /**Indique que la sauvegarde est en cours*/
    isSaving: boolean = false;

    /**Liste des actions du bouton flottant*/
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /**Liste des pourcentages présentée en lignes de deux colonnes*/
    listePourcentagesParLigne: PourcentageDomaine[][] = [];

    /**
     * Constructeur
     */
    constructor(private indemnitesParametresService: IndemnitesParametresService,
                private translateService: TranslateService,
                private toastr: ToastrService) {
    }

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Initialisation des actions
        this.listeActions.next([{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-sauvegarde',
            libelle: 'global.actions.enregistrer',
            doAction: () => this.save(),
            isDisabled: () => this.isLoading || this.isSaving || !this.form?.valid
        }]);

        //Chargement des données
        this.load();
    }

    /**
     * Chargement des données de l'écran
     */
    load(): void {
        //Chargement indépendant des sections pour ne pas bloquer tout l'écran
        this.isLoadingPourcentages = true;
        this.isLoadingReglesConf = true;
        this.updateEtatChargementGlobal();

        //Chargement des pourcentages
        this.indemnitesParametresService.loadListePourcentagesDomaine().subscribe(resultIndemnites => {
            this.isLoadingPourcentages = false;
            this.updateEtatChargementGlobal();

            //Contrôle de la réponse pour la liste des indemnités
            if (resultIndemnites?.codeErreur !== TypeCodeErreur.NO_ERROR) {
                TypeCodeErreur.showError(resultIndemnites?.codeErreur,this.translateService,this.toastr);
                return;
            }

            //Mapping des données serveur vers le modèle manipulé par l'écran
            this.model.listePourcentages = (resultIndemnites?.data?.listePourcentages || []).map(item => new PourcentageDomaine(item));
            this.buildListePourcentagesParLigne();
        },() => {
            //Gestion d'une erreur technique lors du chargement des pourcentages
            this.isLoadingPourcentages = false;
            this.updateEtatChargementGlobal();
            this.toastr.error(this.translateService.instant('global.errors.generic'));
        });

        //Chargement des règles d'attribution
        this.indemnitesParametresService.loadReglesAttributionConf().subscribe(resultRegles => {
            this.isLoadingReglesConf = false;
            this.updateEtatChargementGlobal();

            //Contrôle de la réponse pour les règles d'attribution
            if (resultRegles?.codeErreur !== TypeCodeErreur.NO_ERROR) {
                TypeCodeErreur.showError(resultRegles?.codeErreur,this.translateService,this.toastr);
                return;
            }

            this.model.reglesConf = new ReglesAttributionConf(resultRegles?.data?.conf || {});
        },() => {
            //Gestion d'une erreur technique lors du chargement des règles
            this.isLoadingReglesConf = false;
            this.updateEtatChargementGlobal();
            this.toastr.error(this.translateService.instant('global.errors.generic'));
        });
    }

    //Mise à jour de l'état de chargement global pour le bouton d'enregistrement
    private updateEtatChargementGlobal(): void {
        this.isLoading = this.isLoadingPourcentages || this.isLoadingReglesConf;
    }

    //Construction d'une structure en lignes pour l'affichage des pourcentages par paire
    private buildListePourcentagesParLigne(): void {
        this.listePourcentagesParLigne = [];

        //Aucun élément à afficher
        if (!this.model.listePourcentages?.length) {
            return;
        }

        for (let index = 0; index < this.model.listePourcentages.length; index += 2) {
            this.listePourcentagesParLigne.push(this.model.listePourcentages.slice(index,index + 2));
        }
    }

    //TrackBy pour limiter les re-rendus des lignes de pourcentages
    trackByIndex(index: number): number {
        return index;
    }

    //TrackBy pour limiter les re-rendus des champs de pourcentage
    trackByDomaine(index: number,item: PourcentageDomaine): number {
        return item?.idDomaine ?? index;
    }

    /**
     * Sauvegarde unique de l'écran
     */
    save(): void {
        //Blocage de la sauvegarde si une action est déjà en cours ou si le formulaire est invalide
        if (this.isSaving || this.isLoading || !this.form?.valid) {
            return;
        }

        this.normalizeValeursNumeriques();

        this.isSaving = true;

        //Sauvegarde des pourcentages
        this.indemnitesParametresService.savePourcentages(this.model.listePourcentages).subscribe(resultPourcentages => {
            //Arrêt immédiat si la première sauvegarde retourne une erreur fonctionnelle
            if (resultPourcentages?.codeErreur !== TypeCodeErreur.NO_ERROR) {
                this.isSaving = false;
                TypeCodeErreur.showError(resultPourcentages?.codeErreur,this.translateService,this.toastr);
                return;
            }

            //Sauvegarde de la configuration des règles d'attribution après succès des pourcentages
            this.indemnitesParametresService.saveReglesAttributionConf(this.model.reglesConf).subscribe(resultRegles => {
                this.isSaving = false;

                if (resultRegles?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    //Mise à jour locale avec les données renvoyées par le backend
                    this.model.reglesConf = new ReglesAttributionConf(resultRegles?.data?.reglesConf || this.model.reglesConf);
                    this.toastr.success(this.translateService.instant('global.success.enregistrement'));
                } else {
                    this.toastr.error(this.translateService.instant('admin.frais.indemnites.parametres.erreurs.delaisNonEnregistres'));
                }
            },() => {
                //Gestion d'une erreur technique sur la sauvegarde des règles
                this.isSaving = false;
                this.toastr.error(this.translateService.instant('global.errors.generic'));
            });
        },() => {
            //Gestion d'une erreur technique sur la sauvegarde des pourcentages
            this.isSaving = false;
            this.toastr.error(this.translateService.instant('global.errors.generic'));
        });
    }

    private normalizeValeursNumeriques(): void {
        this.model.reglesConf.delaiDepartTrain = this.toZeroIfEmpty(this.model.reglesConf.delaiDepartTrain);
        this.model.reglesConf.delaiRetourTrain = this.toZeroIfEmpty(this.model.reglesConf.delaiRetourTrain);
        this.model.reglesConf.delaiDepartAvion = this.toZeroIfEmpty(this.model.reglesConf.delaiDepartAvion);
        this.model.reglesConf.delaiRetourAvion = this.toZeroIfEmpty(this.model.reglesConf.delaiRetourAvion);

        this.model.listePourcentages = (this.model.listePourcentages || []).map((item: PourcentageDomaine) => {
            item.pourcentage = this.toZeroIfEmpty(item.pourcentage);
            return item;
        });
    }

    private toZeroIfEmpty(value: number): number {
        return value == null || Number.isNaN(value) ? 0 : value;
    }
}
